import { ChangeEvent } from 'react';

export type EventHandler = (event: any | undefined) => void;

export type EventConfigCTX<T> = {
  [event: string]: EventHandler | T;
  context: T;
};

export type EventConfig = {
  [event: string]: EventHandler;
};

/**
 * Document or Window event registration
 */
const globalEventRegistration: EventConfig = {};

export const addEventHandler = (
  target: EventTarget,
  event: string,
  handler: EventHandler,
) => {
  target.addEventListener(event, handler);
  globalEventRegistration[event] = handler;
};

export const addDomEventHandler = (event: string, handler: EventHandler) => {
  document.addEventListener(event, handler);
  globalEventRegistration[event] = handler;
};

export const hasEventHandler = (event: string) => {
  const handler = globalEventRegistration[event];
  return !!handler;
};

export const removeEventHandler = (
  target: EventTarget,
  event: string,
  handler: EventHandler,
) => {
  target.removeEventListener(event, handler);
  delete globalEventRegistration[event];
};

export const removeDomEventHandler = (event: string, handler: EventHandler) => {
  document.removeEventListener(event, handler);
  delete globalEventRegistration[event];
};

export type TKInputEvent = ChangeEvent<HTMLInputElement | HTMLSelectElement>;

export type TKSelectEvent = ChangeEvent<HTMLSelectElement>;

export enum TKEvent {
  /** browser inactive */
  BLUR = 'blur',
  CLICK = 'click',
  FOCUS = 'focus',
  /** capture `Meta`, `Ctrl`, `Escape`, `Delete` */
  KEYDOWN = 'keydown',
  KEYUP = 'keyup',
  CONTEXTMENU = 'contextmenu',
  POINTERDOWN = 'pointerdown',
  POINTERUP = 'pointerup',
  POINTERMOVE = 'pointermove',
  /** 2025/05/02 */
  TRANSLATEND = 'translateend',
  TRANSLATEST = 'translatestart',
  ZOOMLEVELCHANGE = 'change:resolution',
  ZOOMOUT = 'zoomOut',
  ZOOOMIN = 'zoomIn',
  ZOOMBY = 'zoomBy',
  /** === draw a new fitting(dot) on map */
  NEWELEMENT = 'newElement',
  /** === draw a new length(line) on map */
  NEWLINE = 'singleLine',
  /**
   * == draw a broken line(zigzag), aka multiple segment ==
   * @2025/11/25
   */
  BRKLINE = 'brokenLine',
  /** correct symbol in total correction table */
  CORRECTSYMBOLSILENT = 'correctSymbolSilent',
  /** confirm symbol, mark symbol property: user_validated:1 */
  CONFIRMSYMBOL = 'confirmSymbol',
  CONFIRMSYMBOLS = 'confirmSymbols',
  MAPLOADSTART = 'loadstart',
  MAPLOADEND = 'loadend',
  MAPLOADERROR = 'error',
  /** MAP start zooming or panning */
  MAPMOVESTART = 'movestart',
  /** Map zooming/panning to the end */
  MAPMOVENDED = 'moveend',
  /** Map rendered completed */
  MAPPOSTRENDER = 'postrender',
  /** Map render start */
  MAPPRERENDER = 'prerender',
  /** tile layer Zoomify dispatched event */
  MAPTILELOADERROR = 'tileloaderror',
  /**
   * editor dispatched event
   * @deprecated no longer in use
   */
  MAPLEVELCHANGED = 'mapLevelChanged',
  FEATURECOPY = 'featureCopy',
  /** open property panel */
  FEATURESELECTED = 'featureSelected',
  /** polygon zone selected */
  ZONESELECTED = 'zoneSelected',
  /**
   * close property panel by:
   * - clicking blank space of map, or
   * - feature deletion
   */
  FEATUREDESELECT = 'featureDeselect',
  FEATUREDELETED = 'featureDeleted',
  FEATUREDELETEALL = 'featureDeleteAll',
  FEATUREREPLACE = 'featureReplace',
  /**
   * deselect all from `clear selection` tool
   * @2025/04/17
   */
  DESELECTALL = 'deselectAll',
  /** excluded from selection */
  EXCLUDESELECTION = 'excludeSelection',
  /** toggle lock/unlock on a polygon area feature */
  POLYGON_LOCK_TOGGLE = 'polygonLockToggle',
  /** ? */
  MODIFYFEATUREMODE = 'modifyFeatureMode',
  MOVEFEATUREMODE = 'moveFeatureMode',
  AUTOFEATUREMODE = 'autoFeatureMode',
  REVIEWFITTINGS = 'reviewFittings',
  REVIEWCANCELED = 'reviewCanceled',
  REVIEWAPPLIED = 'reviewApplied',
  REVIEWSYSTEMS = 'reviewSystems',
  FLYTOFEATURE = 'flyToFeature',
  /** fly to feature from total correction table */
  FLYTOFEATUREONLEFT = 'flyToFeatureOnLeft',
  /** notify acitve row change on review panel - 2025/01/29 */
  SELECTSYMBOLROW = 'selectSymbolRow',
  RECHECKFEATURE = 'reCheckFeature',
  UNCHECKFEATURE = 'unCheckFeature',
  FORCEUPDATEMAPSIZE = 'forceUpdateMapSize',
  /** @deprecated in favor of `OPEN_TOTAL_TABLE - @2023/12/07` */
  POPUPDUCTCORRECTMODAL = 'popupDuctCorrect',
  POPUPUNSAVINGMODAL = 'popupUnsavingConfirm',
  PAGESELECTED = 'pageSelected',
  PAGEDESELECT = 'pageDeselected',
  PAGESDISCARD = 'pagesDiscarded',
  PAGESELECTALL = 'pageSelectAllNew',
  PAGEDESELECTALL = 'pageDeselectAllNew',
  /** AI job request sent */
  JOBSTARTED = 'jobStarted',
  /** job status changed - 2024/02/23 */
  JOB_STATUS_CHANGE = 'jobStatusChanged',
  /** job progress notification - 2024/02/23 */
  JOB_PROGRESS_CHANGE = 'jobProgressChanged',
  /** job done with status `complete` - 2024/04/15 */
  JOB_STATUS_DONE = 'jobStatusComplete',
  /** File extraction, and AI job status polling */
  STARTPOLLING = 'startPolling',
  /** AI job  COMPLETED @deprecated */
  STOPPOLLING = 'stopPolling',
  /** AI job error */
  ERRORPOLLING = 'errorPolling',
  FILEXTRACTCOMPLETE = 'fileExtractComplete',
  FILEXTRACTFAILURE = 'fileExtractFailure',
  /** filter symbols by item type */
  FILTERSYMBOLS = 'filterSymbols',
  /** filter symbols by `system` along with item type - 2025/04/02 */
  FILTERWITHSYSTEM = 'filterWithSystem',
  /** filter symbols by `shape` (zShape) along with item type and system */
  FILTERWITHSHAPE = 'filterWithShape',
  /** cover symbols with white */
  REVERSYMBOLS = 'reverseSymbols',
  /** lock symbols that have no `system` assigned */
  LOCKEMPTYSYSTEM = 'lockEmptySystem',
  /** lock symbols that have no `size` assigned */
  LOCKEMPTYSIZE = 'lockEmptySize',
  /** hide text toggling */
  REVERSESIZEDISPLAY = 'reverseSizeText',
  /**
   * Real saving event from `useTopNavBar`, then be processed in `useSymbolSaving`
   *
   * ! SIMPLIFIED EVENT FLOW for auto-saving (as of 2026/01/26):
   *
   * `SCHEDULE_AUTO_SAVING` --> (timeout) --> `SAVECHANGES` (with full detail)
   *
   * SymbolEventExtension now dispatches SAVECHANGES directly with all required
   * parameters (projectId, fileId, pageId, silent), eliminating the intermediate
   * SAVETOTALCORRECTIONS relay event.
   */
  SAVECHANGES = 'saveChanges',
  /**
   * Saving changed symbols done!
   * Dispatched by SymbolEventExtension after save completes.
   * Listened by useSavingEvents to reset saving spinner state.
   * @reactivated 2026/02/17
   */
  SAVECHANGESCOMPLETED = 'saveChangesCompleted',
  /**
   * @deprecated REMOVED - No longer needed as of 2026/01/26
   *
   * This intermediate relay event was eliminated in favor of direct SAVECHANGES
   * dispatch from SymbolEventExtension. The extension now has access to projectId,
   * fileId, and pageId, allowing it to dispatch SAVECHANGES directly with full detail.
   *
   * Previous flow: SCHEDULE_AUTO_SAVING -> SAVETOTALCORRECTIONS -> SAVECHANGES
   * New flow: SCHEDULE_AUTO_SAVING -> SAVECHANGES (directly)
   */
  SAVETOTALCORRECTIONS = 'saveTotalCorrections',
  NEWSYMBOLSAVED = 'newSymbolSaved',
  /** delete one symbol from store in `reducer` by page & symbol */
  CLEARSYMBOL_FROM_STORE = 'clearSymbolFromStore',
  /**
   * Truly remove a symbol from the React store without marking it DEL.
   * Use after an immediate-save deletion where the backend is already committed,
   * so the SavingQueue never sees a stale DEL entry and re-deletes the reused index.
   */
  PURGESYMBOL_FROM_STORE = 'purgeSymbolFromStore',
  /** draw new symbol and cache it to reducer store */
  ADDSYMBOL_TO_STORE = 'addSymbolToStore',
  /** move/resize symbol, and correction properties */
  MODSYMBOL_IN_STORE = 'upadateSymbolInStore',
  /** restore moved symbol from store - @2025/05/02 */
  RESTORE_LAST_SYMBOL = 'undoLastSymbolInStore',
  /**
   * synchronize correction of multiple symbols in store,
   * if event detail is empty, then schedule a auto-saving
   * @date 2024/09/10
   */
  SYNCBATCH_IN_STORE = 'synchronizeBatchInStore',
  /** stroke all the symbols of selected system */
  HIGHLIGHTSYSTEM = 'highlightSystem',
  /** cancel highlighted symbols */
  DELIGHTSYSTEM = 'delightSystem',
  /** batch features selected by drag selection */
  BATCHSELECTION = 'batchFeatureSelected',
  /** batch replace symbols with new item type - @2026/02/20 */
  BATCHREPLACE = 'batchFeaturesReplace',
  /** cache dimension info update to features & symbols to save later */
  BATCHCORRECTION = 'batchFeatureCorrected',
  /** quietly refresh(re-fetch) page symbols AFTER changes(or drawing scale) saved */
  REFRESHSYMBOLS = 'refreshSymbols',
  /**
   * reload symbols after page patched, need spinner support
   * @deprecated in favor of `refreshSymbols`
   */
  RELAODSYMBOLS = 'reloadSymbols',
  /** get mouse position or pulling distance */
  MOUSE_REVEALED = 'mouseRevealed',
  /** retry AI job */
  RESTART_AI_JOB = 'retryAIJob',
  /** set magic brush setting to map */
  APPLY_BRUSH_SETTING = 'applyBrushSetting',
  /** set insulation painting brush setting to map - @2026/08/14 */
  APPLY_INSULATION_PAINTING = 'applyInsulationPainting',
  /** show symbols same as table */
  SYNC_SYMBOLS_FROM_TABLE = 'syncSymbolsFromTable',
  /**
   * mute symbol store changed, unset the global `change` state from `useSymbolsEditStore`
   */
  MUTE_SYMBOL_CHANGE = 'muteSymbolChangeState',
  /**
   * reset modes of specific (already-saved) symbols to INI so they are not
   * re-saved on the next auto-save cycle. Carries { pageId, ids } in detail.
   * @2026/05/19
   */
  MUTE_SAVED_SYMBOLS = 'muteSavedSymbolsState',
  /** toggle item type filter by select symbol id from table view */
  TOGGLE_ITEM_TYPE = 'toggleItemTypeFromSymbolId',
  /** notify drawing that total table opened, then zoom-in drawing to make it larger */
  TOTAL_TABLE_OPENED = 'totalTableOpened',
  /** notify `TotalReviewOpener` to open table correction panel */
  OPEN_TOTAL_TABLE = 'openTotalReviewTable',
  /**
   * Cancel drawing tool, listened in `useNewElementTools`
   * @2025/04/19
   */
  CANCEL_DRAWING_TOOL = 'cancelDrawingTool',
  /**
   * cancel brush tool, deselect tool from map-control section
   * @2025/06/09
   */
  CANCEL_BRUSH_TOOL = 'cancelBrushTool',
  /** cancel saving schedule */
  CANCEL_SAVING_CHANGES = 'holdOffScheduledSaving',
  /** open feature properties panel */
  OPEN_FEATURE_PROPERTIES = 'openFeaturePropertiesPanel',
  /** open batch selected features panel */
  OPEN_BATCH_SELECTIONS = 'openBatchSelectionPanel',
  /** open zone properties panel */
  OPEN_ZONE_PROPERTIES = 'openZonePropertiesPanel',
  /** close zone properties panel */
  CLOSE_ZONE_PROPERTIES = 'closeZonePropertiesPanel',
  /**
   * close feature panels
   * @deprecated not in use
   */
  CLOSE_SIDE_PANEL = 'closeFeatureSelectionPanel',
  /**
   * close all draw tool in use
   */
  CLOSE_ALL_TOOL = 'closeAllDrawTools',
  /** large file uploading progress */
  UPLOAD_PROGRESS = 'uploadProgress',
  /**
   * Schedule an auto-saving on:
   * - Review Panel Closing (closeReviewPanel)
   * - Commander.doScheduledAutoSaving();
   * @2025/07/30
   */
  SCHEDULE_AUTO_SAVING = 'scheduleAutoSaving',
  // ======  TOOLS EVENTS SECTION ====================
  /** == enable `ruler` tool == */
  RULERENABLED = 'rulerEnabled',
  /** == disable `ruler` tool == */
  RULERDISABLED = 'rulerDisabled',
  /** all feature styles were reset; listeners should re-apply any external visibility overrides */
  FEATURES_RERENDERED = 'featuresRerendered',
  /** size change notification to re-render symbols - @2025/08/21 */
  SIZE_FILTER_CHANGED = 'sizeFileChanged',
  /** open size merge tool */
  SIZE_MERGE_OPEN = 'sizeMergeOpen',
  /** close size merge tool */
  SIZE_MERGE_CLOSE = 'sizeMergeClose',
  /** note tool enabled */
  NOTE_TOOL_ENABLED = 'noteToolEnabled',
  /** note tool disabled */
  NOTE_TOOL_DISABLED = 'noteToolDisabled',
  /** open snipping tool - @2026/01/15 */
  SNIP_TOOL_ENABLED = 'snipToolEnabled',
  /** close snipping tool */
  SNIP_TOOL_DISABLED = 'snipToolDisabled',
  /** open polygon drawing tool - @2026/01/27 */
  POLYGON_TOOL_ENABLED = 'polygonToolEnabled',
  /** close polygon drawing tool - @2026/01/27 */
  POLYGON_TOOL_DISABLED = 'polygonToolDisabled',
  /** open insulation area setting modal from wrench button - @2026/04/30 */
  OPEN_INSULATION_AREA_SETTING = 'openInsulationAreaSetting',
  /** open polygon area correct modal from context menu - @2026/06/05 */
  OPEN_POLYGON_CORRECT = 'openPolygonCorrect',
  /** apply insulation area settings and start polygon drawing - @2026/02/12 */
  APPLY_POLYGON_AREA_SETTING = 'applyPolygonAreaSetting',
  /** cancel polygon tool from modal - @2026/02/12 */
  CANCEL_POLYGON_TOOL = 'cancelPolygonTool',
  /** insulation prop from zone panel changed - @2026/02/27 */
  POLYGON_PROP_CHANGED = 'polygonPropChanged',
  /** system-based insulation dictionary updated from zone panel - @2026/07/31 */
  POLYGON_DICT_CHANGED = 'polygonDictChanged',
  /** generate VIRTUAL insulaiton symbols - @2026/02/27 */
  VIRT_INSULATIONS_GEN = 'virtualInsulationGen',
  /** update settings on an existing VIRTUAL insulation symbol - @2026/08/25 */
  VIRT_INSULATIONS_UPD = 'virtualInsulationUpd',
  /** delete all VIRTUAL insulation symbols - @2026/02/27 */
  VIRT_INSULATIONS_DEL = 'virtualInsulationCleanupAll',
  /** detach one insulation & relationship  @2026/03/11 */
  VIRT_INSULATION_DTC = 'virtualInsulationDetach',
  /** cut partially-intersecting DUCT_LENGTH boxes by polygon boundary - @2026/05/27 */
  ZONE_SCISSORS = 'zoneScissors',
  /** attach two SIDE_TAKEOFF symbols to DUCT_LENGTH box sides - @2026/05/28 */
  STO_ATTACH = 'sideTakeoffAttach',
  /** remove attached SIDE_TAKEOFF symbols from DUCT_LENGTH - @2026/05/28 */
  STO_DETACH = 'sideTakeoffDetach',
  /** draw an END_CAP symbol in the center of LENGTH symbol - @2025/11/06 */
  ENDCAP_ATTACH = 'endcapAttach',
  ENDCAP_DETACH = 'endcapDetach',
  /** toggle diagonal visual on DUCT_LENGTH rect symbol - @2026/03/06 */
  DIAGONAL_ATTACH = 'diagonalAttach',
  DIAGONAL_DETACH = 'diagonalDetach',
  /** highlight symbol from zone panel - @2026/02/05 */
  HIGHLIGHT_SYMBOL = 'highlightSymbol',
  /** de-highlight symbol from zone panel - @2026/02/05 */
  DEHIGHLIGHT_SYMBOL = 'deHighlightSymbol',
  // ======  OL `Draw` INTERACTION NATIVE EVENTS ====================
  /** ol/interaction/Draw: sketch drawing started */
  DRAWSTART = 'drawstart',
  /** ol/interaction/Draw: sketch drawing finished */
  DRAWEND = 'drawend',
  /** ol/interaction/Draw: sketch drawing aborted */
  DRAWABORT = 'drawabort',
}
